import { defineFurniture } from './types.js';
import type { FurnitureDef } from './types.js';

/**
 * Singapore-themed decorations and the kopitiam cafe set.
 * Owned by the art feature; drawers live in the client's art/singapore.ts.
 */
export const SINGAPORE_FURNITURE: Record<string, FurnitureDef> = {
  /* The cafe set: a cosy couch lounge for the main hangout. */
  ...defineFurniture('seating', {
    couch_left: {
      label: 'Orange Couch (Left)',
      description: 'The famous orange couch. Line up left, middle and right pieces.',
      price: 450,
      solid: false,
      seat: 'facing',
      height: 0.5,
    },
    couch_middle: {
      label: 'Orange Couch (Middle)',
      description: 'The best seat in the house, obviously.',
      price: 450,
      solid: false,
      seat: 'facing',
      height: 0.5,
    },
    couch_right: {
      label: 'Orange Couch (Right)',
      description: 'The end of the couch, with a comfy armrest.',
      price: 450,
      solid: false,
      seat: 'facing',
      height: 0.5,
    },
    bar_stool: {
      label: 'Bar Stool',
      description: 'Perch at the counter and watch the barista work.',
      price: 150,
      solid: false,
      seat: 'free',
      height: 0.7,
    },
    void_deck_bench: {
      label: 'Void Deck Bench',
      description: 'Cool terrazzo, the uncles gather here after dinner.',
      price: 200,
      solid: false,
      seat: 'facing',
      height: 0.45,
    },
    mrt_bench: {
      label: 'MRT Bench',
      description: 'Please give up this seat to those who need it more.',
      price: 260,
      solid: false,
      seat: 'facing',
      height: 0.45,
    },
  }),
  ...defineFurniture('decor', {
    coffee_table: {
      label: 'Low Coffee Table',
      description: 'For mugs, magazines and feet (when nobody is looking).',
      price: 250,
      solid: true,
      height: 0.45,
    },
    cafe_window: {
      label: 'Cafe Window',
      description: 'A big window with a striped awning and a view of the street.',
      price: 600,
      solid: true,
      height: 2.2,
    },
    stage_floor: {
      label: 'Stage Boards',
      description: 'Wooden stage flooring for open mic night. Walk right on.',
      price: 180,
      solid: false,
      height: 0,
    },
    mic_stand: {
      label: 'Mic Stand',
      description: 'Open mic night! Sing your heart out.',
      price: 350,
      solid: true,
      height: 1.6,
    },
    chalkboard_menu: {
      label: 'Chalkboard Menu',
      description: 'Today: kopi, teh, kaya toast, and good company.',
      price: 300,
      solid: true,
      height: 1.6,
    },
  }),
  /* Singapore icons. */
  ...defineFurniture('singapore', {
    merlion: {
      label: 'Merlion Statue',
      description: 'Half lion, half fish, fully iconic. (Water feature not included.)',
      price: 3000,
      solid: true,
      height: 2.6,
    },
    hdb_block: {
      label: 'HDB Block Model',
      description: 'A tiny, colourful home for millions of Singaporeans.',
      price: 1200,
      solid: true,
      height: 2.4,
    },
    void_deck_table: {
      label: 'Void Deck Chess Table',
      description: 'A stone table with a chessboard top. Stakes: bragging rights.',
      price: 250,
      solid: true,
      height: 0.8,
    },
    dragon_slide: {
      label: 'Dragon Playground',
      description: 'The mosaic dragon slide every Singaporean kid remembers.',
      price: 2500,
      solid: true,
      height: 1.8,
    },
    durian_stall: {
      label: 'Durian Stall',
      description: 'The king of fruits. Not allowed on the MRT.',
      price: 700,
      solid: true,
      height: 1.3,
    },
    orchid_pot: {
      label: 'Orchid Pot',
      description: 'Vanda Miss Joaquim, the national flower.',
      price: 180,
      solid: true,
      height: 1.3,
    },
    sg_flag: {
      label: 'Singapore Flag',
      description: 'Red and white, a crescent and five stars. Majulah!',
      price: 300,
      solid: true,
      height: 2.4,
    },
    red_lanterns: {
      label: 'Red Lanterns',
      description: 'A pair of festive lanterns on a wooden stand.',
      price: 220,
      solid: true,
      height: 2,
    },
    peranakan_rug: {
      label: 'Peranakan Tiles',
      description: 'Colourful floor tiles from a Joo Chiat shophouse.',
      price: 350,
      solid: false,
      height: 0,
    },
    kopitiam_sign: {
      label: 'Kopitiam Signboard',
      description: 'A classic red signboard. Open 6am till late.',
      price: 400,
      solid: true,
      height: 2,
    },
    mama_shop_shelf: {
      label: 'Mama Shop Shelf',
      description: 'Sweets, snacks, stamps and everything else you forgot.',
      price: 650,
      solid: true,
      height: 1.8,
    },
    satay_grill: {
      label: 'Satay Grill',
      description: 'Smoky sticks of goodness with peanut sauce.',
      price: 550,
      solid: true,
      height: 1,
    },
    bird_cage: {
      label: 'Songbird Cage',
      description: "An uncle's pride and joy, singing every morning.",
      price: 380,
      solid: true,
      height: 1.9,
    },
    kueh_display: {
      label: 'Kueh Display',
      description: 'Ang ku kueh, ondeh-ondeh and kueh lapis under glass.',
      price: 320,
      solid: true,
      height: 1.1,
    },
    chope_table: {
      label: 'Choped Table',
      description: 'A marble table with a tissue packet on it. This table is taken.',
      price: 280,
      solid: true,
      height: 0.9,
    },
    rain_tree: {
      label: 'Rain Tree',
      description: 'A shady giant from the Botanic Gardens.',
      price: 900,
      solid: true,
      height: 3,
    },
    bicycle: {
      label: 'Bicycle',
      description: 'For a ride along the park connector.',
      price: 400,
      solid: true,
      height: 1.1,
    },
  }),
};
